
import Vue from 'vue';
import { env } from "@/env";
import store from '@/store';
import RepsolNavBarComponent from '@/components/repsol-nav-bar/RepsolNavBarComponent.vue';
import RepsolDialogOkComponent from '@/components/repsol-dialog-ok/RepsolDialogOkComponent.vue';
import RepsolDialog from '@/components/RepsolDialog.vue';
import RepsolBreadcrumbComponent from '@/components/repsol-breadcrumb/RepsolBreadcrumbComponent.vue';
import { UserIdentityModel } from '@/models/file-entities.model';
import { RouteType } from '@/models/file-enum.model';
import { DialogConfig } from '@/models/file-app.model';
import { checkRouteRedirect } from '@/utils/Navigation';
import { isMobile } from '@/utils/Breakpoints';
import { stringUTCToDate } from '@/utils/Dates';
import { BreadcrumbItem } from '@/models/types';
import { changeLocale, getTopLanguages, normalizeLocale } from '@/utils/Locale';
export default Vue.extend({
    name: 'RepsolLayoutComponent',
    components: {
        RepsolNavBarComponent,
        RepsolDialogOkComponent,
        RepsolDialog,
        RepsolBreadcrumbComponent,
    },

    data() {
        const languages = getTopLanguages();
        const profileLanguage = normalizeLocale(((store.state as any).userIdentity?.userIdentity?.idioma ?? (store.state as any).userIdentity?.userIdentity?.Idioma ?? 'es'));

        return {
            showDialogChangesForm: false,
            menuExpanded: !isMobile(),
            icons: false,
            selectedLanguage: profileLanguage === 'en' ? 'en' : 'es',
            navigatorLanguage: navigator.language.substr(0, 2),
            profileLanguage: profileLanguage === 'en' ? 'en' : 'es',
            languages,
            timer: null as any,
            tokenExpiredDialog: false,
            appName: env.appName !== '' ? env.appName : 'RepsolProject',
            environment: env.environment,
            dialogConfig: {
                canClose: true,
                title: 'confirm_actions.exit_form',
                subtitle: 'lost_changes',
                secondaryBtnText: 'actions_list.cancel',
                mainBtnText: 'actions_list.exit_without_save',
            } as DialogConfig,
            breadcrumbList: [] as BreadcrumbItem[],
        };
    },

    computed: {
        userIdentity(): UserIdentityModel {
            return (store.state as any).userIdentity.userIdentity as UserIdentityModel;
        },
        isOpenDialog(): boolean {
            return (store.state as any).dialogForm.isOpenDialog;
        },
        isMobileSize(): boolean {
            if (isMobile()) {
                (this as any).menuExpanded = false;
            }
            return isMobile();
        },
        username(): string {
            const userIdentity = (store.state as any).userIdentity.userIdentity;
            return String(userIdentity?.name ?? '');
        },
        rol(): string {
            const userIdentity = (store.state as any).userIdentity.userIdentity;
            if (!userIdentity) return '';
            if (!userIdentity.mesaId) return userIdentity.perfilId;

            return `${userIdentity.perfilId} (${userIdentity.mesaId})`;
        },
        userRoles(): string {
            const user = (this as any).auth?.user;
            if (user && user.idToken) {
                return user.idToken.roles > 0 ? user.idToken.roles[0] : 'None';
            }
            return '';
        },
        languageOptions(): { text: string; value: string }[] {
            return (this as any).languages.map((language: string) => ({
                value: language,
                text: this.$t(`languages.${language}`).toString(),
            }));
        },
        selectedLanguageText(): string {
            const selected = (this as any).languageOptions.find((item: { text: string; value: string }) => item.value === (this as any).selectedLanguage);
            return selected?.text ?? String((this as any).selectedLanguage || '').toUpperCase();
        },
    },

    watch: {
        isOpenDialog(): void {
            this.showDialogChangesForm = !this.showDialogChangesForm;
        },
        '$i18n.locale'(newLocale: string): void {
            const normalizedLocale = normalizeLocale(newLocale) === 'en' ? 'en' : 'es';
            this.selectedLanguage = normalizedLocale;
        },
    },

    mounted(): void {
        if (Object.keys(this.userIdentity).length <= 0) {
            checkRouteRedirect(RouteType.Login);
        }

        const timeSession: number = stringUTCToDate(this.userIdentity.tokenExpirationDate).getTime() - new Date().getTime();

        if (timeSession <= 0) {
            this.tokenExpiredDialog = true;
        } else {
            this.timer = setInterval(() => (this.tokenExpiredDialog = true), Math.min(timeSession, 2147483647));
        }

        this.profileLanguage = this.getProfileLanguage();
        this.selectedLanguage = this.profileLanguage;

        this.setHeight();

        window.addEventListener('resize', () => {
            this.setHeight();
        });

        const parts = this.$router.currentRoute.fullPath.split('/');
        this.breadcrumbList = parts.map((part) => ({ text: part, disabled: false, href: part }));
    },

    methods: {
        getProfileLanguage(): string {
            const userIdentity = this.userIdentity as any;
            const language = normalizeLocale(userIdentity?.idioma ?? userIdentity?.Idioma ?? 'es');
            return language === 'en' ? 'en' : 'es';
        },

        async setLanguage(language: string): Promise<void> {
            const normalizedLanguage = normalizeLocale(language) === 'en' ? 'en' : 'es';

            if (!this.userIdentity?.id) {
                this.selectedLanguage = this.profileLanguage;
                return;
            }

            if (normalizedLanguage === this.profileLanguage) {
                this.selectedLanguage = normalizedLanguage;
                changeLocale(normalizedLanguage, true);
                return;
            }

            this.selectedLanguage = normalizedLanguage;

            const updatedUser = await store.dispatch('userIdentity/updateIdioma', {
                id: this.userIdentity.id,
                idioma: normalizedLanguage,
            });

            if (updatedUser) {
                this.profileLanguage = normalizedLanguage;
                this.selectedLanguage = normalizedLanguage;
                changeLocale(normalizedLanguage, true);
                return;
            }

            this.selectedLanguage = this.profileLanguage;
        },

        async logoutAsync(): Promise<void> {
            try {
                (store.state as any).loader.isLoading = true;
                clearInterval(this.timer);
                sessionStorage.clear();
                (store.state as any).userIdentity.userIdentity = {} as UserIdentityModel;
                checkRouteRedirect(RouteType.Login);
                (store.state as any).loader.isLoading = false;
            } catch (error: any) {
                const messageError = 'response_error.logout';
                this.$toast.error(this.$t(messageError));
                (store.state as any).loader.isLoading = false;
            }
        },

        setHeight(): number {
            const height = window.innerHeight - 110;

            const mainSectionDOM = document.getElementById('mainSection');

            if (mainSectionDOM != undefined) {
                mainSectionDOM.style.height = height + 'px';
            }

            return window.innerHeight - 110;
        },

        expiresIn(expirationDate: string): number {
            return stringUTCToDate(expirationDate).getTime() - new Date().getTime();
        },
    },
});
